import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import type { Item } from '../../domain/inventory';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';

interface DeleteFailure {
  id: string;
  message: string;
}

interface DeleteResult {
  succeeded: string[];
  failed: DeleteFailure[];
}

interface DeleteItemsDialogProps {
  items: Item[];
  onClose: () => void;
  // Only called once every item in `items` was deleted successfully -- a
  // partial failure leaves the dialog open showing which ids failed instead,
  // since callers (e.g. the item detail page) use this to decide whether it's
  // safe to navigate away.
  onDeleted: (deletedIds: string[]) => void;
}

/**
 * There is no batch-delete RPC (unlike batchUpdateItems/batchCreateItems,
 * which are each one atomic request) -- DeleteItem is a single-row soft
 * delete, so a multi-item selection runs one call per item and can genuinely
 * partially fail. This surfaces that rather than pretending it's atomic.
 */
export function DeleteItemsDialog({ items, onClose, onDeleted }: DeleteItemsDialogProps) {
  const queryClient = useQueryClient();
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [failures, setFailures] = useState<DeleteFailure[]>([]);
  const isBulk = items.length > 1;

  const mutation = useMutation({
    mutationFn: async (): Promise<DeleteResult> => {
      const results = await Promise.allSettled(
        items.map((item) => inventoryApi.deleteItem(item.id)),
      );
      const succeeded: string[] = [];
      const failed: DeleteFailure[] = [];
      results.forEach((result, index) => {
        const id = items[index]!.id;
        if (result.status === 'fulfilled') {
          succeeded.push(id);
        } else {
          const reason = result.reason as unknown;
          failed.push({
            id,
            message: reason instanceof Error ? reason.message : 'Delete failed.',
          });
        }
      });
      return { succeeded, failed };
    },
    onSuccess: async ({ succeeded, failed }) => {
      if (succeeded.length > 0) {
        for (const id of succeeded) queryClient.removeQueries({ queryKey: ['item', id] });
        // See EditItemDrawer's onSuccess for why the full-fleet query is
        // invalidated separately with refetchType: 'none'.
        await queryClient.invalidateQueries({ queryKey: ['items'] });
        void queryClient.invalidateQueries({
          queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
          refetchType: 'none',
        });
      }
      if (failed.length > 0) {
        setFailures(failed);
        return;
      }
      onDeleted(succeeded);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFailures([
        {
          id: items[0]?.id ?? '',
          message: error instanceof Error ? error.message : 'The item could not be deleted.',
        },
      ]);
    },
  });

  const settled = failures.length > 0 || unknownOutcome;
  const title = isBulk ? `Delete ${items.length.toLocaleString()} items` : `Delete ${items[0]?.id}`;

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <div
        className="confirmation-dialog"
        role="dialog"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close delete dialog"
          >
            ×
          </button>
        </div>
        <div className="warning-callout">
          This permanently removes {isBulk ? 'these items' : 'this item'} from inventory and cannot
          be undone.
        </div>
        {isBulk ? (
          <ul className="batch-item-list">
            {items.map((item) => (
              <li key={item.id} className="batch-item-row">
                <span className="mono">{item.id}</span>
                {failures.some((failure) => failure.id === item.id) ? (
                  <span className="batch-stale-tag">failed</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['items'] });
              onClose();
            }}
          />
        ) : null}
        {failures.length > 0 ? (
          <div role="alert" className="form-error">
            <p>
              {failures.length === items.length
                ? 'Nothing was deleted.'
                : `${items.length - failures.length} of ${items.length} deleted; ${failures.length} failed.`}
            </p>
            <ul>
              {failures.map((failure) => (
                <li key={failure.id}>
                  {failure.id}: {failure.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            {settled ? 'Close' : 'Cancel'}
          </button>
          {settled ? null : (
            <button
              type="button"
              className="button-danger"
              onClick={() => {
                setUnknownOutcome(false);
                setFailures([]);
                mutation.mutate();
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? 'Deleting…'
                : isBulk
                  ? `Delete ${items.length.toLocaleString()} items`
                  : 'Delete item'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
